import { useCallback } from "react";

import {
  FieldPathValue,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";

import { BulletinFormValues } from "@/components/alerts/add-v2/model/types";

/**
 * The seam between the section components and the form state.
 *
 * Sections take no value props — README decision 14. Phase 1 backed these exports with plain React
 * state and phase 2 re-implemented them on react-hook-form, with no section signature changing;
 * that is what phase 0 existed to guarantee.
 *
 * Unlike Formik, react-hook-form subscribes per field: `useWatch({ name })` re-renders its caller
 * only when *that* field changes. The isolation README decision 8 asks for therefore comes from this
 * hook itself, and a section gets it by reading one field per component rather than by being wrapped
 * in anything. Its converse is the rule to keep in mind: a component calling `useBulletinValues`
 * re-renders on every keystroke on the page.
 */

export type SetValue<K extends keyof BulletinFormValues> = (
  value:
    | BulletinFormValues[K]
    | ((previous: BulletinFormValues[K]) => BulletinFormValues[K]),
) => void;

/** Resolves the functional-updater form of a setter against the value it was called with. */
export const applyValueUpdate = <K extends keyof BulletinFormValues>(
  value: Parameters<SetValue<K>>[0],
  previous: BulletinFormValues[K],
): BulletinFormValues[K] =>
  typeof value === "function"
    ? (value as (p: BulletinFormValues[K]) => BulletinFormValues[K])(previous)
    : value;

/**
 * Read and write one top-level field.
 *
 * For arrays and for controls that are not a single `<input>` — the autocompletes, the row lists.
 * A plain text or select input should use `fields/BulletinTextField` instead, which registers itself
 * and so also gets focus handling and, from phase 3, its own error.
 */
export const useBulletinField = <K extends keyof BulletinFormValues>(
  name: K,
): [BulletinFormValues[K], SetValue<K>] => {
  const { setValue, getValues } = useFormContext<BulletinFormValues>();
  const value = useWatch({ name }) as BulletinFormValues[K];

  const set = useCallback<SetValue<K>>(
    (next) =>
      setValue(
        name,
        // Read through `getValues` rather than closing over the watched value: it is the current
        // value by definition, which both keeps this setter's identity stable across renders and
        // makes two writes in one handler compose.
        //
        // The cast is unavoidable, not a widening: react-hook-form types the value as
        // `FieldPathValue<T, K>`, a conditional type TypeScript cannot reduce while `K` is still a
        // type parameter. It resolves to exactly `BulletinFormValues[K]` at every call site.
        applyValueUpdate<K>(next, getValues(name)) as FieldPathValue<
          BulletinFormValues,
          K
        >,
        { shouldDirty: true, shouldValidate: true },
      ),
    [setValue, getValues, name],
  );

  return [value, set];
};

/**
 * The whole document. Only for consumers that genuinely need all of it — the live preview and the
 * completion counter — and both are expected to defer or memoize. A section reaching for this is a
 * mistake: it subscribes to every field at once.
 *
 * `useWatch` and not `watch()`: `watch()` re-renders whichever component called `useForm`, which is
 * the provider, and the provider's children are a stable element that React then reconciles past —
 * so the caller would never see the new values. The cast is because react-hook-form types a
 * name-less watch as `DeepPartial`, having no way to know the default values are complete; ours
 * always are, because `BulletinFormProvider` seeds a full `BulletinFormValues`.
 */
export const useBulletinValues = (): BulletinFormValues => {
  const { control } = useFormContext<BulletinFormValues>();
  return useWatch({ control }) as BulletinFormValues;
};

/**
 * The error react-hook-form currently holds for one top-level field, already translated.
 *
 * For the controls that `fields/BulletinTextField` cannot cover — the autocompletes, which are not
 * a single registered `<input>` and so have no `useController` of their own to ask.
 *
 * `useFormState({ name })` and not `formState` off the context: the latter subscribes the caller to
 * every field's state, which would undo the isolation the hook above exists to provide.
 *
 * It answers "what should this input show", which is not the same question as "what is the draft
 * still missing" — with a resolver, react-hook-form writes an error only for the field that
 * changed, plus everything a `trigger()` filled in. `validation/sectionErrors.ts` answers the
 * second question, from the schema.
 */
export const useBulletinFieldError = (
  name: keyof BulletinFormValues,
): string | undefined => {
  const { errors } = useFormState<BulletinFormValues>({ name });
  return errors[name]?.message as string | undefined;
};
